import type { WebsocketChannels } from "@bbj-types/websockets/body";
import { WebsocketSendActions } from "@bbj-types/websockets/subscribe";
import type {
  PublishWebsocketCandidateHandler,
  PublishWebsocketDocumentHandler,
  PublishWebsocketEventHandler,
  PublishWebsocketNotificationHandler,
  PublishWebsocketSMSStatusUpdateHandler,
} from "@bbj-types/window";
import { useEffect, useRef } from "react";

type WebsocketHandler<Channel extends WebsocketChannels> = Channel extends WebsocketChannels.EVENT
  ? PublishWebsocketEventHandler
  : Channel extends WebsocketChannels.CANDIDATE
    ? PublishWebsocketCandidateHandler
    : Channel extends WebsocketChannels.SMS_STATUS_UPDATE
      ? PublishWebsocketSMSStatusUpdateHandler
      : Channel extends WebsocketChannels.DOCUMENT
        ? PublishWebsocketDocumentHandler
        : PublishWebsocketNotificationHandler;

export const manageHandlerWebsocket = <Channel extends WebsocketChannels>(
  channel: Channel,
  handler: WebsocketHandler<Channel>,
  sendChannel: string,
  websocketAction: WebsocketSendActions.SUBSCRIBE | WebsocketSendActions.UNSUBSCRIBE
) => {
  if (window.wsClient?.isConnected()) {
    if (websocketAction === WebsocketSendActions.SUBSCRIBE) {
      window.subscribeWebsocket(channel, handler);
    } else if (websocketAction === WebsocketSendActions.UNSUBSCRIBE) {
      window.unsubscribeWebsocket(channel, handler);
    }
    window.wsClient.sendJson({
      action: websocketAction,
      channel: sendChannel,
    });
  }
};

export const useWebsocketHandler = <Channel extends WebsocketChannels>(
  channel: Channel,
  sendChannel: string,
  handler: WebsocketHandler<Channel>
) => {
  // biome-ignore lint/suspicious/noExplicitAny: The any is needed for the wrapper function. Without it I can't follow arguments.
  const handlerRef = useRef<(...args: any) => void>(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const handlerWrapper: typeof handlerRef.current = (...args) => handlerRef.current(...args);
    manageHandlerWebsocket(
      channel,
      handlerWrapper as WebsocketHandler<Channel>,
      sendChannel,
      WebsocketSendActions.SUBSCRIBE
    );
    return () => {
      manageHandlerWebsocket(
        channel,
        handlerWrapper as WebsocketHandler<Channel>,
        sendChannel,
        WebsocketSendActions.UNSUBSCRIBE
      );
    };
  }, [channel, sendChannel]);
};
